import React from 'react';
import DataTable from '../../reporting/reporting_utils/DataTable';
import { Common } from '../../utils/common';
import moment from 'moment';

const $ = window.$;

/** @type {State} */
const initialState = {
  updateType: '',
  validationResult: null,
  error: null,
  selectedFile: undefined,
  validateIsLoading: false,
  saveIsLoading: false,
  confirmInput: '',
  showSuccessAlert: false,
  showErrorAlert: false,
};

/**
 * @param {State} state
 * @param {Action} action
 * @returns {State}
 */
function reducer(state, action) {
  switch (action.type) {
    case 'UPDATE_TYPE_CHANGED':
      return { ...state, updateType: action.payload, validationResult: null, error: null };
    case 'FILE_INPUT_CHANGED':
      return { ...state, selectedFile: action.payload, validationResult: null, error: null };
    case 'VALIDATE_CSV':
      return {
        ...state,
        error: null,
        validationResult: null,
        validateIsLoading: true,
        showErrorAlert: false,
        showSuccessAlert: false,
      };
    case 'VALIDATE_CSV_SUCCESS':
      return { ...state, validateIsLoading: false, validationResult: action.payload };
    case 'VALIDATE_CSV_FAILED':
      return { ...state, validateIsLoading: false, error: action.payload };
    case 'CONFIRM_INPUT_CHANGED':
      return { ...state, confirmInput: action.payload };
    case 'SAVE_CHANGES':
      return { ...state, saveIsLoading: true };
    case 'SAVE_CHANGES_SUCCESS':
      return { ...initialState, showSuccessAlert: true };
    case 'SAVE_CHANGES_FAILED':
      return { ...state, saveIsLoading: false, showErrorAlert: true, confirmInput: '' };
    default:
      throw new Error(`Invalid action ${action}`);
  }
}

const updateTypes = /** @type {const} */ ([
  { label: 'Select an update type', value: '' },
  { label: 'Target Update', value: 'target_update' },
  { label: 'Campaign Feature Update', value: 'campaign_feature_changes' },
  { label: 'Add Webhook', value: 'add_webhook' },
  { label: 'Assign Webhook to Campaign', value: 'change_campaign_webhook' },
  { label: 'Change LeadScore Webhook', value: 'change_leadscore_webhook' },
  { label: 'Change DealSaver Webhook', value: 'change_dealsaver_webhook' },
]);

export default function MassEdit() {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const fileInputRef = React.useRef(null);

  const hasValidationErrors = Boolean(
    state.validationResult && state.validationResult.errorCount > 0
  );

  const columnDefs = React.useMemo(() => {
    /** @type {import('../../reporting/reporting_utils/DataTable').ColumnDef[]} */
    let columns = [];

    switch (state.updateType) {
      case 'target_update':
        columns = [
          {
            data: 'status',
            title: 'Error Status',
            visible: hasValidationErrors,
            render(data) {
              return `<i class='${
                data === 'Errored' ? 'glyphiconno glyphicon-remove' : 'glyphiconyes glyphicon-ok'
              }'></i>`;
            },
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          {
            data: 'error_reason',
            title: 'Error Reason',
            visible: hasValidationErrors,
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          { data: 'group_name', title: 'Group Name' },
          { data: 'auto_group_id', title: 'Group ID' },
          { data: 'location_name', title: 'Location Name' },
          { data: 'domain_id', title: 'Domain ID' },
          { data: 'did', title: 'Current Phone Number' },
          { data: 'new_ring_to_number', title: 'New Ring to Number' },
          { data: 'new_ring_to_domain', title: 'New Ring to Domain' },
        ];
        break;
      case 'campaign_feature_changes':
        columns = [
          {
            data: 'status',
            title: 'Error Status',
            visible: hasValidationErrors,
            render(data) {
              return `<i class='${
                data === 'Errored' ? 'glyphiconno glyphicon-remove' : 'glyphiconyes glyphicon-ok'
              }'></i>`;
            },
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          {
            data: 'error_reason',
            title: 'Error Reason',
            visible: hasValidationErrors,
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          { data: 'location_name', title: 'Location Name' },
          { data: 'group_name', title: 'Group Name' },
          { data: 'auto_group_id', title: 'Group ID' },
          { data: 'domain_id', title: 'Domain ID' },
          {
            data: 'did',
            title: 'Phone Number',
            render(data) {
              return Common.formatPhoneNumber(data);
            },
          },
          {
            data: 'callshield',
            title: 'Callshield',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
          },
          {
            data: 'call_recording',
            title: 'Call Recording',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
          },
          {
            data: 'transcription',
            title: 'Transcription',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
          },
          {
            data: 'leadscore',
            title: 'Leadscore',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
          },
          {
            data: 'call_disclaimer',
            render(data) {
              if (data === 0) {
                return 'None';
              } else if (data === 1) {
                return 'English';
              } else if (data === 2) {
                return 'Spanish';
              } else if (data === 3) {
                return 'French';
              } else {
                return null;
              }
            },
            title: 'Call Disclaimer',
          },
          {
            data: 'target_id',
            title: 'Target ID',
            render(data) {
              if (data === 0) {
                return '<i class="glyphiconno glyphicon-remove"></i>';
              } else {
                return data;
              }
            },
          },
          { data: 'assigned_campaign_id', title: 'Assigned Campaign ID' },
          {
            data: 'change_date',
            title: 'Change Date',
            render: (data) =>
              data ? moment.utc(data).local().format('MMM D, YYYY hh:mm:ss A') : null,
          },
          {
            data: 'old_callshield',
            title: 'Old Callshield',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
            visible: false,
          },
          {
            data: 'old_call_recording',
            title: 'Old Call Recording',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
            visible: false,
          },
          {
            data: 'old_transcription',
            title: 'Old Transcription',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
            visible: false,
          },
          {
            data: 'old_leadscore',
            title: 'Old Leadscore',
            render(data) {
              if (data === null) {
                return null;
              } else {
                return `<i class="${
                  data === 1 ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                }"></i>`;
              }
            },
            visible: false,
          },
          {
            data: 'old_call_disclaimer',
            title: 'Old Call Disclaimer',
            render(data) {
              if (data === 0) {
                return 'None';
              } else if (data === 1) {
                return 'English';
              } else if (data === 2) {
                return 'Spanish';
              } else if (data === 3) {
                return 'French';
              } else {
                return null;
              }
            },
            visible: false,
          },
          {
            data: 'old_target_id',
            title: 'Old Target ID',
            visible: false,
          },
        ];
        break;
      case 'add_webhook':
        columns = [
          {
            data: 'status',
            title: 'Error Status',
            visible: hasValidationErrors,
            render(data) {
              return `<i class='${
                data === 'Errored' ? 'glyphiconno glyphicon-remove' : 'glyphiconyes glyphicon-ok'
              }'></i>`;
            },
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          {
            data: 'error_reason',
            title: 'Error Reason',
            visible: hasValidationErrors,
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          { data: 'group_name', title: 'Group Name' },
          { data: 'auto_group_id', title: 'Group ID' },
          { data: 'location_name', title: 'Location Name' },
          { data: 'domain_id', title: 'Domain ID' },
          { data: 'action_name', title: 'Action Name' },
          { data: 'action_template_id', title: 'Template ID' },
          { data: 'url', title: 'URL' },
          {
            data: 'headers',
            title: 'Headers',
            render(data) {
              return `<pre>${data}</pre>`;
            },
          },
          { data: 'success_code', title: 'Success Response Code' },
        ];
        break;
      case 'change_campaign_webhook':
        columns = [
          {
            data: 'status',
            title: 'Error Status',
            visible: hasValidationErrors,
            render(data) {
              return `<i class='${
                data === 'Errored' ? 'glyphiconno glyphicon-remove' : 'glyphiconyes glyphicon-ok'
              }'></i>`;
            },
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          {
            data: 'error_reason',
            title: 'Error Reason',
            visible: hasValidationErrors,
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          { data: 'action_id', title: 'Action ID' },
          { data: 'auto_group_id', title: 'Group ID' },
          { data: 'domain_id', title: 'Domain ID' },
          {
            data: 'did',
            title: 'DID',
            render(data) {
              return Common.formatPhoneNumber(data);
            },
          },
          { data: 'action_timing', title: 'Action Timing' },
        ];
        break;
      case 'change_leadscore_webhook':
      case 'change_dealsaver_webhook':
        columns = [
          {
            data: 'status',
            title: 'Error Status',
            visible: hasValidationErrors,
            render(data) {
              return `<i class='${
                data === 'Errored' ? 'glyphiconno glyphicon-remove' : 'glyphiconyes glyphicon-ok'
              }'></i>`;
            },
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          {
            data: 'error_reason',
            title: 'Error Reason',
            visible: hasValidationErrors,
            className: hasValidationErrors ? 'text-danger text-center' : 'text-center',
          },
          { data: 'action_id', title: 'Action ID' },
          { data: 'auto_group_id', title: 'Group ID' },
          { data: 'domain_id', title: 'Domain ID' },
          { data: 'call_type_id', title: 'Call Type ID' },
        ];
        break;
    }

    return columns;
  }, [hasValidationErrors, state.updateType]);

  const createdRow = React.useCallback((row, data) => {
    if (data.status === 'Errored') {
      $(row).addClass('danger');
    }
  }, []);

  async function validateCSV() {
    if (!state.selectedFile) {
      return;
    }

    const formData = new FormData();
    formData.append('file', state.selectedFile);
    formData.append('update_type', state.updateType);

    try {
      dispatch({ type: 'VALIDATE_CSV' });

      const response = await fetch('/admin/mass_edit/validate', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
        },
      });

      const data = await response.json();

      if (response.ok) {
        /** @type {ValidationResult} */
        const payload = {
          runId: data.change_run.change_run_id,
          errorCount: data.validation_errors,
          previewData: data.validation_results,
        };

        dispatch({ type: 'VALIDATE_CSV_SUCCESS', payload });
      } else {
        dispatch({ type: 'VALIDATE_CSV_FAILED', payload: data.error });
      }
    } catch (error) {
      console.error(error);
      dispatch({ type: 'VALIDATE_CSV_FAILED', payload: 'Error validating csv file' });
    }
  }

  async function saveChanges() {
    if (!state.validationResult) {
      return;
    }

    try {
      const body = {
        change_run_id: state.validationResult.runId,
        update_type: state.updateType,
      };

      dispatch({ type: 'SAVE_CHANGES' });
      const response = await fetch('/admin/mass_edit/update', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        fileInputRef.current.value = '';
        dispatch({ type: 'SAVE_CHANGES_SUCCESS' });
      } else {
        dispatch({ type: 'SAVE_CHANGES_FAILED' });
      }
    } catch (error) {
      console.error(error);
      dispatch({ type: 'SAVE_CHANGES_FAILED' });
    } finally {
      $('#confirm-modal').modal('hide');
    }
  }

  let updateTypeDescription = '';
  switch (state.updateType) {
    case 'target_update':
      updateTypeDescription =
        'Mass updates campaign DIDs to a new target. Upload a csv with values for domain_id, group_id, did, new_ring_to_number and new_ring_to_domain';
      break;
    case 'campaign_feature_changes':
      updateTypeDescription =
        'Mass updates campaign features. Upload a csv with values for group_id, domain_id, did, callshield, call_recording, call_disclaimer, transcription, leadscore, and target_id';
      break;
    case 'add_webhook':
      updateTypeDescription =
        'Mass add webhooks. Upload a csv with values for group_id, domain_id, name, action_template_id, url, success_reponse_code, http_method, http_header_name_1, http_header_value_1, http_header_name_2, http_header_value_2, http_header_name_3, http_header_value_3, http_header_name_4, http_header_value_4, http_header_name_5, http_header_value_5';
      break;
    case 'change_campaign_webhook':
      updateTypeDescription =
        'Mass assign webhooks to campaigns. Upload a csv with values for group_id, domain_id, did, action_id, and action_timing';
      break;
    case 'change_leadscore_webhook':
      updateTypeDescription =
        'Mass add webhooks to LeadScore call types. Upload a csv with values for group_id, domain_id, action_id, call_type_id';
      break;
    case 'change_dealsaver_webhook':
      updateTypeDescription =
        'Mass add webhooks to DealSaver call types. Upload a csv with values for group_id, domain_id, action_id, call_type_id';
  }

  return (
    <>
      {state.showSuccessAlert ? (
        <div className='alert alert-success alert-dismissible' role='alert'>
          <button type='button' className='close' data-dismiss='alert' aria-label='Close'>
            <span aria-hidden='true'>&times;</span>
          </button>
          Your changes were saved successfully.
        </div>
      ) : state.showErrorAlert ? (
        <div className='alert alert-danger alert-dismissible' role='alert'>
          <button type='button' className='close' data-dismiss='alert' aria-label='Close'>
            <span aria-hidden='true'>&times;</span>
          </button>
          Failed to save changes.
        </div>
      ) : null}
      <div style={{ paddingRight: 32, paddingLeft: 32 }}>
        <div className='row'>
          <div className='col-xs-12'>
            <h3>Mass Edit</h3>
            <p>Select an update type and upload a csv</p>
          </div>
        </div>
        <div className='row' style={{ marginTop: 15 }}>
          <div className='col-sm-6'>
            <div className='form-group'>
              <label htmlFor='update-type' className='form-label'>
                Update Type
              </label>
              <select
                value={state.updateType}
                onChange={(event) => {
                  const updateType = /** @type {UpdateType} */ (event.target.value);
                  dispatch({ type: 'UPDATE_TYPE_CHANGED', payload: updateType });
                }}
                className='form-control'
                id='update-type'
                name='update-type'
                aria-describedby='update-type-description'
              >
                {updateTypes.map((updateType) => (
                  <option key={updateType.value} value={updateType.value}>
                    {updateType.label}
                  </option>
                ))}
              </select>
              {state.updateType ? (
                <span id='update-type-description' className='help-block'>
                  {updateTypeDescription}
                </span>
              ) : null}
            </div>
          </div>
          <div className='col-sm-6'>
            <div className={`form-group ${state.error ? 'has-error' : ''}`}>
              <label htmlFor='csv-file' className='form-label'>
                Upload CSV
              </label>
              <input
                ref={fileInputRef}
                className='form-control'
                type='file'
                id='csv-file'
                name='csv-file'
                accept='.csv'
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    dispatch({ type: 'FILE_INPUT_CHANGED', payload: file });
                  }
                }}
                disabled={!state.updateType}
                aria-describedby='csv-file-help-block'
              />
              {state.error ? (
                <span id='csv-file-help-block' className='help-block'>
                  {state.error}
                </span>
              ) : null}
            </div>
            {state.selectedFile && state.updateType ? (
              <button
                className='btn btn-primary pull-right'
                onClick={validateCSV}
                disabled={state.validateIsLoading}
              >
                Preview Changes
              </button>
            ) : null}
          </div>
        </div>
        {hasValidationErrors ? (
          <div className='row' style={{ marginTop: 15 }}>
            <div className='col-xs-12'>
              <div className='alert alert-danger' role='alert'>
                Your file contains {state.validationResult?.errorCount} invalid rows. Please fix
                them and upload a new csv.
              </div>
            </div>
          </div>
        ) : null}
        {state.validationResult ? (
          <>
            <div className='row' style={{ marginTop: 32 }}>
              <div className='col-xs-12'>
                <h3>Preview</h3>
                <DataTable
                  columnDefs={columnDefs}
                  data={state.validationResult.previewData}
                  title='Mass Edit Preview'
                  selectColumns={true}
                  exportData={false}
                  stateSave={false}
                  createdRow={createdRow}
                />
              </div>
            </div>
            <div className='row' style={{ marginTop: 15 }}>
              <div className='col-xs-12'>
                {hasValidationErrors ? null : (
                  <button
                    className='btn btn-success pull-right'
                    data-toggle='modal'
                    data-target='#confirm-modal'
                  >
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>

      <div
        id='confirm-modal'
        className='modal fade'
        tabIndex={-1}
        role='dialog'
        aria-labelledby='modal-title'
      >
        <div className='modal-dialog modal-sm' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <button type='button' className='close' data-dismiss='modal' aria-label='Close'>
                <span aria-hidden='true'>&times;</span>
              </button>
              <h4 className='modal-title' id='modal-title'>
                Confirm Mass Edit
              </h4>
            </div>
            {state.validationResult ? (
              <div className='modal-body'>
                <p>
                  Are you sure you want to update {state.validationResult.previewData.length}{' '}
                  locations? This action cannot be undone.
                </p>
                <p>To confirm, type &quot;Confirm&quot; in the box below.</p>
                <input
                  type='text'
                  className='form-control'
                  value={state.confirmInput}
                  onChange={(event) => {
                    dispatch({ type: 'CONFIRM_INPUT_CHANGED', payload: event.target.value });
                  }}
                />
              </div>
            ) : null}
            <div className='modal-footer'>
              <button type='button' className='btn btn-default' data-dismiss='modal'>
                Close
              </button>
              <button
                type='button'
                className='btn btn-success'
                disabled={state.confirmInput !== 'Confirm' || state.saveIsLoading}
                onClick={() => saveChanges()}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * @typedef {Object} ValidationResult
 * @property {number} runId
 * @property {number} errorCount
 * @property {array} previewData
 */

/**
 * @typedef {typeof updateTypes[number]['value']} UpdateType
 */

/**
 * @typedef {Object} State
 * @property {UpdateType} updateType
 * @property {ValidationResult | null} validationResult
 * @property {string | null} error
 * @property {File | undefined} selectedFile
 * @property {boolean} validateIsLoading
 * @property {boolean} saveIsLoading
 * @property {string} confirmInput
 * @property {boolean} showSuccessAlert
 * @property {boolean} showErrorAlert
 */

/**
 * @typedef {(
 *  | { type: 'UPDATE_TYPE_CHANGED', payload: UpdateType }
 *  | { type: 'FILE_INPUT_CHANGED', payload: File }
 *  | { type: 'VALIDATE_CSV'}
 *  | { type: 'VALIDATE_CSV_SUCCESS', payload: ValidationResult }
 *  | { type: 'VALIDATE_CSV_FAILED', payload: string }
 *  | { type: 'CONFIRM_INPUT_CHANGED', payload: string }
 *  | { type: 'SAVE_CHANGES' }
 *  | { type: 'SAVE_CHANGES_SUCCESS' }
 *  | { type: 'SAVE_CHANGES_FAILED' }
 * )} Action
 */
